import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { CLIENT_HEALTH_LABEL } from "@/lib/enumLabels";

// Health → numeric score for plotting (higher = healthier)
const HEALTH_SCORE = { strong: 4, stable: 3, at_risk: 2, critical: 1 };
// Y-axis tick label lookup: score → the humanised health label.
const SCORE_TO_LABEL = { 4: "Strong", 3: "Stable", 2: "At risk", 1: "Critical" };

/**
 * Resolves the runtime value of a CSS custom property (e.g. `--chart-2`) so
 * Recharts — which needs a real color string — can render theme-aware
 * strokes and fills. Recomputed whenever the caller re-renders after a
 * theme flip.
 */
function useCssVar(...names) {
  const [values, setValues] = useState({});
  useEffect(() => {
    const cs = getComputedStyle(document.documentElement);
    const next = {};
    for (const n of names) {
      const v = cs.getPropertyValue(n).trim();
      // Vars are stored as `H S% L%`; wrap for CSS color functions.
      next[n] = v ? `hsl(${v})` : "";
    }
    setValues(next);
    // Watch for theme toggles via the html attribute change.
    const obs = new MutationObserver(() => {
      const cs2 = getComputedStyle(document.documentElement);
      const nn = {};
      for (const n of names) {
        const v = cs2.getPropertyValue(n).trim();
        nn[n] = v ? `hsl(${v})` : "";
      }
      setValues(nn);
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "class"] });
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return values;
}

export default function HealthTrend({ clientId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const tokens = useCssVar("--chart-2", "--chart-5", "--muted-foreground", "--border", "--card", "--brass");
  const line = tokens["--chart-2"] || "hsl(218 45% 60%)";
  const alert = tokens["--chart-5"] || "hsl(0 68% 55%)";
  const muted = tokens["--muted-foreground"] || "hsl(35 8% 62%)";
  const border = tokens["--border"] || "hsl(30 6% 28%)";
  const card = tokens["--card"] || "hsl(220 38% 17%)";
  const brass = tokens["--brass"] || "hsl(32 44% 48%)";
  const activeDot = useMemo(() => brass, [brass]); // hovered point promotes to brass = "look here"

  useEffect(() => {
    let cancelled = false;
    api.get(`/clients/${clientId}/health-history`).then((r) => {
      if (cancelled) return;
      const points = [...(r.data.history || [])];
      const last = points[points.length - 1];
      const cur = r.data.current;
      if (!last || last.health !== cur.health || last.date !== cur.date) {
        points.push(cur);
      }
      setData(points.map((p, i) => ({
        idx: i,
        date: new Date(p.date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" }),
        rawDate: p.date,
        score: HEALTH_SCORE[p.health] ?? 3,
        health: p.health,
        changed_by: p.changed_by || "—",
      })));
      setLoading(false);
    }).catch(() => setLoading(false));
    return () => { cancelled = true; };
  }, [clientId]);

  if (loading) return <div className="border border-border bg-card p-8 text-xs text-muted-foreground text-center">Loading trend…</div>;
  if (data.length === 0) return <div className="border border-border bg-card p-8 text-xs text-muted-foreground text-center">No health history yet.</div>;

  return (
    <div className="border border-border bg-card">
      <div className="border-b border-border px-5 py-3">
        <h3 className="font-display font-bold text-sm tracking-tight">Relationship health trend</h3>
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">{data.length} data points · auto-tracked on every health change</div>
      </div>
      <div className="p-5 h-72">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={border} strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="date" stroke={muted} tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
            <YAxis
              domain={[0.5, 4.5]}
              ticks={[1, 2, 3, 4]}
              tickFormatter={(v) => SCORE_TO_LABEL[v] || ""}
              stroke={muted}
              tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }}
              width={70}
            />
            {/* at_risk threshold — semantic red at low opacity */}
            <ReferenceLine y={2} stroke={alert} strokeDasharray="3 3" opacity={0.35} />
            <Tooltip
              contentStyle={{ backgroundColor: card, border: `1px solid ${border}`, borderRadius: 0, fontSize: 12, fontFamily: "IBM Plex Sans" }}
              labelStyle={{ color: muted, fontFamily: "IBM Plex Mono", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em" }}
              formatter={(v, _, p) => [CLIENT_HEALTH_LABEL[p.payload.health] || p.payload.health, "Health"]}
            />
            <Line
              type="stepAfter"
              dataKey="score"
              stroke={line}
              strokeWidth={2}
              dot={{ fill: line, r: 4, strokeWidth: 0 }}
              activeDot={{ r: 6, fill: activeDot }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="border-t border-border">
        <table className="w-full text-sm">
          <thead className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
            <tr className="border-b border-border">
              <th className="text-left px-5 py-2 font-normal">Date</th>
              <th className="text-left px-3 py-2 font-normal">Health</th>
              <th className="text-left px-5 py-2 font-normal">Changed by</th>
            </tr>
          </thead>
          <tbody>
            {[...data].reverse().map((d, i) => (
              <tr key={i} className="border-b border-border/50">
                <td className="px-5 py-2 data-cell text-xs">{d.date}</td>
                <td className="px-3 py-2 text-xs uppercase font-mono tracking-widest text-foreground">{CLIENT_HEALTH_LABEL[d.health] || d.health}</td>
                <td className="px-5 py-2 text-xs">{d.changed_by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
